import React, { useEffect, useState } from 'react';
import { getAuthHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { formatMagentoDateTime } from '../utils/datetime';
import { getOrderAssignment } from '../utils/orderMetrics';
import {
  choosePreferredPickingOrder,
  readActivePickingOrderId,
  readPickingQueueContext,
  saveActivePickingOrder,
} from '../utils/pickingNavigation';
import LoadingOverlay from '../components/LoadingOverlay';
import './OrderListPage.css';

const normalizeUser = (value) => {
  if (!value) return '';
  return String(value).trim().toLowerCase();
};

const formatDateTime = (input) => formatMagentoDateTime(input);

const OrderListPage = ({ isAdmin = false }) => {
  // ---- estado base ----
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // seleção + envio
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shippingFilter, setShippingFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [pickingProgress, setPickingProgress] = useState({ active: false, current: 0, total: 0 });
  const [isResuming, setIsResuming] = useState(false);

  // usuário atual (salvo no login)
  const rawUsername = localStorage.getItem('operator') || localStorage.getItem('username') || '';
  const currentUsername = rawUsername.trim() || 'picker';
  const currentUsernameKey = normalizeUser(currentUsername);
  const userDisplayName = currentUsername || 'Unknown user';

  // configurable picking status code (default: 'picking')
  const pickingStatus = (localStorage.getItem('pickingStatus') || 'picking').trim();

  // REST base (use /rest/all/V1 to avoid store-scope issues)
  const restBase = (localStorage.getItem('restBase') || '/rest/V1').trim() || '/rest/V1';

  const matchesShippingFilter = (order) => {
    if (!order) return false;
    const shipping = (order.shipping_description || '').toLowerCase();
    if (shippingFilter === 'dpd') return shipping.includes('dpd');
    if (shippingFilter === 'xpect') return shipping.includes('dublin');
    if (shippingFilter === 'click') return shipping.includes('pickup');
    return true; // 'all'
  };

  const getShippingLabel = (order) => {
    if (!order) return 'N/A';
    const raw = order.shipping_description || '';
    const shipping = raw.toLowerCase();
    if (shipping.includes('pickup')) return 'Click & Collect';
    if (shipping.includes('dublin')) return 'Xpect';
    return raw || 'N/A';
  };

  const filteredOrders = orders.filter(matchesShippingFilter);
  const selectedCount = selectedOrders.length;

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const sortedOrders = React.useMemo(() => {
    const list = [...filteredOrders];
    if (!list.length) return list;

    if (sortConfig.column === 'order') {
      list.sort((a, b) => {
        const sign = sortConfig.direction === 'asc' ? 1 : -1;
        return sign * (a.increment_id || '').localeCompare(b.increment_id || '');
      });
    } else if (sortConfig.column === 'purchaseDate') {
      list.sort((a, b) => {
        const aTime = new Date(a.created_at).getTime();
        const bTime = new Date(b.created_at).getTime();
        const sign = sortConfig.direction === 'asc' ? 1 : -1;
        return sign * (aTime - bTime);
      });
    }

    return list;
  }, [filteredOrders, sortConfig]);

  // ---- ações de UI ----
  const normalizeEntityId = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const toggleOrderSelection = (order) => {
    const entityKey = normalizeEntityId(order?.entity_id ?? order?.id);
    if (!entityKey) return;
    setSelectedOrders((prev) =>
      prev.includes(entityKey)
        ? prev.filter((id) => id !== entityKey)
        : [...prev, entityKey]
    );
  };

  useEffect(() => {
    setSelectedOrders((prev) => {
      const allowed = new Set(
        filteredOrders.map((order) => normalizeEntityId(order?.entity_id ?? order?.id)).filter(Boolean)
      );
      return prev.filter((id) => allowed.has(id));
    });
  }, [shippingFilter, orders]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('operator');
    try {
      sessionStorage.removeItem('adminPanelAccessUser');
    } catch {
      // ignore
    }
    window.location.href = '/';
  };

  /**
   * Move cada ordem selecionada para "picking" gravando comentário:
   * POST /V1/orders/{entity_id}/comments
   * body: { statusHistory: { status, comment, ... } }
   */
  const handleStartPicking = async () => {
    if (selectedOrders.length === 0) return;
    setIsSubmitting(true);
    const totalSelected = selectedOrders.length;
    setPickingProgress({ active: true, current: 0, total: totalSelected });
    let hadSuccessRedirect = false;
    try {
      // Map entity_id -> order (quick lookup for display + navigation)
      const mapByEntity = orders.reduce((acc, o) => {
        const key = normalizeEntityId(o?.entity_id ?? o?.id);
        if (key) acc[key] = o;
        return acc;
      }, {});

      const successes = [];
      const successIncrements = [];

      let processedCount = 0;
      const advanceProgress = () => {
        processedCount += 1;
        setPickingProgress((prev) => ({
          ...prev,
          current: Math.min(processedCount, totalSelected),
        }));
      };

      for (const entityKey of selectedOrders) {
        if (!entityKey) {
          advanceProgress();
          continue;
        }

        let order = mapByEntity[entityKey];
        if (!order) {
          try {
            const fetchRes = await apiFetch(`${restBase}/orders/${encodeURIComponent(entityKey)}`, {
              headers: getAuthHeaders(),
            });
            if (fetchRes.ok) {
              order = await fetchRes.json();
            }
          } catch (err) {
            console.warn('Failed to fetch order by entity_id:', entityKey, err);
          }
        }

        const orderEntityId = order?.entity_id ?? order?.id ?? Number(entityKey);
        if (!orderEntityId || Number.isNaN(Number(orderEntityId))) {
          console.warn('Missing entity_id for order', entityKey);
          advanceProgress();
          continue;
        }

        const incrementId = order?.increment_id ?? entityKey;
        const latestAssignment = getOrderAssignment(order).currentOperator;
        const latestAssignmentKey = normalizeUser(latestAssignment);
        const currentStatusKey = (order?.status || '').toLowerCase();
        const pickingStatusKey = pickingStatus.toLowerCase();
        if (
          currentStatusKey === pickingStatusKey &&
          latestAssignmentKey &&
          latestAssignmentKey !== currentUsernameKey
        ) {
          alert(`Order ${incrementId} is already assigned to ${latestAssignment}.`);
          advanceProgress();
          continue;
        }

        const entityPath = encodeURIComponent(entityKey);

        // 2) Try statusHistory endpoint first (preferred path)
        const comment = `Assigned to ${currentUsername} — picking workflow started`;
        let postText = '';
        let putText = '';
        let updated = false;
        let commentAdded = false;

        const postRes = await apiFetch(`${restBase}/orders/${entityPath}/comments`, {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            statusHistory: {
              status: pickingStatus,
              comment,
              is_visible_on_front: 0,
              is_customer_notified: 0,
            },
          }),
        });

        if (postRes.ok) {
          updated = true;
          commentAdded = true;
        } else {
          try { postText = await postRes.text(); } catch (_) {}
          console.warn('Status update via comments failed:', incrementId, postText || postRes.status);
        }

        if (!updated) {
          // 2c) New recommendation: POST /orders with entity payload (entity_id + status + state)
          const postEntityPayload = {
            entity: {
              entity_id: Number(orderEntityId) || orderEntityId,
              status: pickingStatus,
              state: 'processing', // advice from Magento team: picking status lives under processing state
            },
          };

          const postEntityRes = await apiFetch(`${restBase}/orders`, {
            method: 'POST',
            headers: getAuthHeaders(true),
            body: JSON.stringify(postEntityPayload),
          });

          if (postEntityRes.ok) {
            updated = true;
            console.log('✅ POST /orders entity update succeeded for', incrementId);
          } else {
            let postEntityText = '';
            try { postEntityText = await postEntityRes.text(); } catch (_) {}
            console.warn('POST /orders payload failed:', incrementId, postEntityText || postEntityRes.status);
            postText = postText || postEntityText;
          }
        }

        if (updated && !commentAdded) {
          try {
            const commentRes = await apiFetch(`${restBase}/orders/${entityPath}/comments`, {
              method: 'POST',
              headers: getAuthHeaders(true),
              body: JSON.stringify({
                statusHistory: {
                  status: pickingStatus,
                  comment,
                  is_visible_on_front: 0,
                  is_customer_notified: 0,
                },
              }),
            });
            if (commentRes.ok) {
              commentAdded = true;
            } else {
              console.warn('Secondary comment attempt failed:', incrementId, await commentRes.text());
            }
          } catch (secondaryErr) {
            console.warn('Secondary comment attempt threw error:', incrementId, secondaryErr);
          }
        }

        if (!updated) {
          // 2d) Legacy fallback: PUT /orders/{id} with status only (may fail if Magento blocks it)
          const putRes = await apiFetch(`${restBase}/orders/${entityPath}`, {
            method: 'PUT',
            headers: getAuthHeaders(true),
            body: JSON.stringify({
              entity: {
                entity_id: Number(orderEntityId) || orderEntityId,
                status: pickingStatus,
              },
            }),
          });

          if (putRes.ok) {
            updated = true;
          } else {
            try { putText = await putRes.text(); } catch (_) {}
            console.error('Fallback PUT status failed:', incrementId, putText || putRes.status);
          }
        }

        if (!updated) {
          if (successes.length === 0) {
            const postPart = postText ? `POST/Entity response: ${postText}` : '';
            const putPart = putText ? `\nPUT response: ${putText}` : '';
            const extra = (postPart + putPart).trim() || 'No response body';
            alert(
              `Failed to set status to "${pickingStatus}" for order ${incrementId}.\n` +
              extra
            );
          }
          advanceProgress();
          continue;
        }

        // Optional verification (best-effort)
        try {
          const verifyRes = await apiFetch(`${restBase}/orders/${entityPath}`, {
            headers: getAuthHeaders(),
          });
          const verifyData = await verifyRes.json();
          console.log(`✅ Order ${incrementId} updated to status: ${verifyData.status}, state: ${verifyData.state}`);
        } catch (verr) {
          console.warn('⚠️ Failed to verify updated status for order', incrementId, verr);
        }

        successes.push(entityKey);
        if (incrementId) successIncrements.push(incrementId);
        advanceProgress();
      }

      if (successes.length > 0) {
        // remove from local list & navigate to the first successful selection
        const remaining = orders.filter((o) => {
          const key = normalizeEntityId(o?.entity_id ?? o?.id);
          return !successes.includes(key);
        });
        setOrders(remaining);

        const firstInc = successIncrements.find(Boolean);
        setSelectedOrders([]);
        setPickingProgress((prev) => ({
          ...prev,
          current: prev.total || successes.length,
        }));
        const targetHref = firstInc ? `/picking/${firstInc}` : '/picking';
        if (firstInc) saveActivePickingOrder(firstInc);
        hadSuccessRedirect = true;
        setTimeout(() => {
          setPickingProgress({ active: false, current: 0, total: 0 });
          window.location.href = targetHref;
        }, 150);
      } else {
        alert('No order was moved to Picking. Check errors in the console.');
      }
    } catch (e) {
      console.error(e);
      alert('Error starting picking. See console for details.');
    } finally {
      setIsSubmitting(false);
      if (!hadSuccessRedirect) {
        setPickingProgress({ active: false, current: 0, total: 0 });
      }
    }
  };

  /**
   * Opens the current user's picking queue, keeping the last active position.
   */
  const handleResumePicking = async () => {
    // Trava reentrancia: enquanto uma busca esta em andamento, ignora cliques extras
    // (evita varios alerts empilhados quando nao ha ordens).
    if (isResuming) return;
    setIsResuming(true);

    // Busca os detalhes de uma lista de ordens em paralelo e mantem so as atribuidas ao usuario.
    const loadAssignedDetails = async (list = []) => {
      const details = await Promise.all(list.map(async (o) => {
        try {
          const detRes = await apiFetch(`${restBase}/orders/${o.entity_id}`, {
            headers: getAuthHeaders(),
          });
          if (!detRes.ok) return null;
          return await detRes.json();
        } catch (err) {
          console.warn('Failed to fetch order detail during resume:', o.entity_id, err);
          return null;
        }
      }));
      return details.filter(Boolean).filter((det) => {
        const assignedKey = normalizeUser(getOrderAssignment(det).currentOperator);
        return assignedKey && assignedKey === currentUsernameKey;
      });
    };

    const openTarget = (list) => {
      const targetId = choosePreferredPickingOrder(list, {
        activeId: readActivePickingOrderId(),
        context: readPickingQueueContext(),
      });
      const target = list.find((order) => String(order.increment_id) === String(targetId)) || list[0];
      saveActivePickingOrder(target);
      window.location.href = `/picking/${target.increment_id}`;
    };

    const fetchOrdersByStatus = async (statusValue) => {
      const url =
        `${restBase}/orders?` +
        'searchCriteria[filter_groups][0][filters][0][field]=status' +
        `&searchCriteria[filter_groups][0][filters][0][value]=${encodeURIComponent(statusValue)}` +
        '&searchCriteria[filter_groups][0][filters][0][condition_type]=eq';
      const resp = await apiFetch(url, { headers: getAuthHeaders() });
      const data = await resp.json();
      return data.items || [];
    };

    try {
      // 1) Ordens no status de picking atribuidas ao usuario
      const pickingOrders = await fetchOrdersByStatus(pickingStatus);
      const assignedDetails = await loadAssignedDetails(pickingOrders);
      if (assignedDetails.length) {
        openTarget(assignedDetails);
        return;
      }

      // 2) Fallback: alguns stores nao tem status "picking"; tenta processing + comentario de atribuicao
      const processingOrders = await fetchOrdersByStatus('processing');
      const fallbackDetails = await loadAssignedDetails(processingOrders);
      if (fallbackDetails.length) {
        openTarget(fallbackDetails);
        return;
      }

      alert('No picking order assigned to you was found. Select orders and click Start Picking.');
    } catch (e) {
      console.error(e);
      alert('Error fetching your picking orders.');
    } finally {
      setIsResuming(false);
    }
  };

  // ---- carregar ordens em "processing" ----
  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // const token = await getToken();

        // Apenas status=processing
        const url =
          `${restBase}/orders?` +
          'searchCriteria[filter_groups][0][filters][0][field]=status' +
          '&searchCriteria[filter_groups][0][filters][0][value]=processing' +
          '&searchCriteria[filter_groups][0][filters][0][condition_type]=eq';

        const response = await apiFetch(url, {
          headers: getAuthHeaders(),
        });

        const data = await response.json();
        // Segurança extra no cliente
        const onlyProcessing = (data.items || []).filter(
          (o) => (o.status || '').toLowerCase() === 'processing'
        );
        setOrders(onlyProcessing);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Error fetching orders');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, []);


  // ---- render ----
  return (
    <div className="order-page">
      <div className="order-page-inner">
      <header className="order-top">
        <div className="order-headline">
          <h1>Processing Orders</h1>
          <p>
            {filteredOrders.length === 0
              ? 'No orders match the current filters.'
              : `${filteredOrders.length} order${filteredOrders.length === 1 ? '' : 's'} ready to assign.`}
          </p>
        </div>
        <div className="order-buttons">
          <button
            className="resume-button action-button"
            onClick={handleResumePicking}
            disabled={isResuming}
            title="Open the first picking order assigned to you"
          >
            {isResuming ? 'Resuming…' : 'Resume Picking'}
          </button>
          <button
            className="picking-button action-button"
            onClick={handleStartPicking}
            disabled={selectedCount === 0 || isSubmitting}
            title={
              selectedCount === 0
                ? 'Select at least one order'
                : 'Move selected orders to Picking'
            }
          >
            {isSubmitting ? 'Starting…' : `Start Picking${selectedCount ? ` (${selectedCount})` : ''}`}
          </button>
        </div>
      </header>

      <section className="order-toolbar">
        <div className="order-stats">
          <div className="stat-card">
            <span className="stat-label">Visible</span>
            <span className="stat-value">{filteredOrders.length}</span>
          </div>
          <div className={`stat-card ${selectedCount ? 'is-active' : ''}`}>
            <span className="stat-label">Selected</span>
            <span className="stat-value">{selectedCount}</span>
          </div>
        </div>

        <div className="order-filter">
          <label htmlFor="shippingFilter">Shipping</label>
          <select
            id="shippingFilter"
            value={shippingFilter}
            onChange={(e) => setShippingFilter(e.target.value)}
          >
            <option value="all">All methods</option>
            <option value="dpd">DPD</option>
            <option value="xpect">Xpect</option>
            <option value="click">Click & Collect</option>
          </select>
        </div>
      </section>

      <LoadingOverlay show={isLoading && !pickingProgress.active} />
      {error && !isLoading && <div className="order-state order-state--error">{error}</div>}

      {!isLoading && !error && filteredOrders.length === 0 && (
        <div className="order-state order-state--empty">
          <h3>Nothing to show yet</h3>
          <p>Adjust the filters or refresh to pick up newly imported orders.</p>
        </div>
      )}

      {!isLoading && !error && filteredOrders.length > 0 && (
        <div className="order-table-card">
          <table className="order-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={
                      filteredOrders.length > 0 &&
                      selectedOrders.length === filteredOrders.length
                    }
                    onChange={() => {
                      if (selectedOrders.length === filteredOrders.length) {
                        setSelectedOrders([]);
                      } else {
                        const allIds = filteredOrders
                          .map((o) => normalizeEntityId(o?.entity_id ?? o?.id))
                          .filter(Boolean);
                        setSelectedOrders(allIds);
                      }
                    }}
                  />
                </th>
                <th>
                  <button
                    type="button"
                    className={`sort-header ${sortConfig.column === 'order' ? 'is-active' : ''}`}
                    onClick={() => handleSort('order')}
                  >
                    Order
                    <span className="sort-indicator">
                      {sortConfig.column === 'order'
                        ? sortConfig.direction === 'asc' ? '↑' : '↓'
                        : '↕'}
                    </span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className={`sort-header ${sortConfig.column === 'purchaseDate' ? 'is-active' : ''}`}
                    onClick={() => handleSort('purchaseDate')}
                  >
                    Purchase Date
                    <span className="sort-indicator">
                      {sortConfig.column === 'purchaseDate'
                        ? sortConfig.direction === 'asc' ? '↑' : '↓'
                        : '↕'}
                    </span>
                  </button>
                </th>
                <th>Customer</th>
                <th>Status</th>
                <th>Shipping</th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map((order) => (
                <tr key={order.increment_id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(
                        normalizeEntityId(order?.entity_id ?? order?.id)
                      )}
                      onChange={() => toggleOrderSelection(order)}
                    />
                  </td>
                  <td>
                    <div className="order-cell">
                      <span className="order-id">#{order.increment_id}</span>
                      <span className="order-sub">{order.store_name || order.store_code || 'Default Store'}</span>
                    </div>
                  </td>
                  <td>{formatDateTime(order.created_at)}</td>
                  <td>
                    <div className="order-cell">
                      <span className="order-main">{order.customer_firstname} {order.customer_lastname}</span>
                      {order.customer_email && <span className="order-sub">{order.customer_email}</span>}
                    </div>
                  </td>
                  <td><span className="status-tag">{order.status}</span></td>
                  <td>{getShippingLabel(order)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      <LoadingOverlay show={pickingProgress.active}>
        <div className="picking-progress-text">
          <p>Moving orders to Picking…</p>
          <p className="picking-progress-count">
            {Math.min(pickingProgress.current, pickingProgress.total)}/{pickingProgress.total || 0}
          </p>
        </div>
      </LoadingOverlay>

      <LoadingOverlay show={isResuming} ariaLabel="Looking for your picking orders">
        <div className="picking-progress-text">
          <p>Looking for your picking orders…</p>
        </div>
      </LoadingOverlay>
    </div>
  );
};

export default OrderListPage;


// indentificar o problema da ordem por que esta dando o erro de que quando uma ordem e solta e outro usuario
// tentar pegar a ordem fala que esta designado para o antigo que soltou a ordem. 
